/*
 * The pharmacy's month: every home's order deadlines and cycle starts, and what has actually been
 * done — orders in, trays made up and checked, deliveries sent and booked in.
 *
 * Deadlines stand out and done things recede, and a deadline whose day has come with nothing
 * recorded is marked late, because that is the one thing on the page that needs chasing. On a
 * narrow screen the month grid gives way to a list of the days that have something on them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from './api.ts';
import { addDays, dayOf, longDay, todayKey } from './days.ts';
import type { Go } from './pharmacy-routes.ts';

export type CalendarEvent = {
  date: string;
  kind:
    | 'order-by' | 'cycle-starts' | 'ordered' | 'prepared' | 'checked' | 'delivered'
    | 'delivery-sent' | 'delivery-booked-in';
  state: 'upcoming' | 'late' | 'met' | 'happened';
  homeId: string;
  homeName: string;
  label: string;
  cycleStart: string | null;
  deliveryId: string | null;
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHOWN_PER_DAY = 3;

export function CalendarPage({ go, month }: { go: Go; month: string | null }) {
  const today = todayKey();
  const shown = month ?? today.slice(0, 7);
  const grid = useMemo(() => gridFor(shown), [shown]);

  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [error, setError] = useState('');
  const [homeFilter, setHomeFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ events: CalendarEvent[] }>(
        `/api/pharmacy/calendar?from=${grid.days[0]}&to=${grid.days[grid.days.length - 1]}`,
      );
      setEvents(data.events);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the calendar.');
    }
  }, [grid]);

  useEffect(() => {
    setEvents(null);
    setSelected(today.startsWith(shown) ? today : null);
    void load();
  }, [load, shown, today]);

  const homes = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of events ?? []) seen.set(e.homeId, e.homeName);
    return [...seen].sort((a, b) => a[1].localeCompare(b[1]));
  }, [events]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of events ?? []) {
      if (homeFilter && e.homeId !== homeFilter) continue;
      const list = map.get(e.date) ?? [];
      list.push(e);
      map.set(e.date, list);
    }
    return map;
  }, [events, homeFilter]);

  const open = (e: CalendarEvent) =>
    go(
      e.deliveryId
        ? { page: 'home', homeId: e.homeId, tab: 'deliveries', deliveryId: e.deliveryId }
        : { page: 'home', homeId: e.homeId, tab: 'cycles', deliveryId: null },
    );

  const lateCount = [...byDay.values()].flat().filter((e) => e.state === 'late').length;
  const agendaDays = grid.days.filter((d) => d.startsWith(shown) && byDay.has(d));

  return (
    <section>
      <div className="head">
        <div>
          <h1>Calendar</h1>
          <p className="muted">Order deadlines and cycle starts for every home, and what has been done.</p>
        </div>
      </div>

      <div className="cal-bar">
        <div className="cal-nav">
          <button type="button" className="quiet" aria-label="Previous month" onClick={() => go({ page: 'calendar', month: stepMonth(shown, -1) })}>
            ‹
          </button>
          <h2 className="cal-month">{grid.label}</h2>
          <button type="button" className="quiet" aria-label="Next month" onClick={() => go({ page: 'calendar', month: stepMonth(shown, 1) })}>
            ›
          </button>
          {!today.startsWith(shown) ? (
            <button type="button" className="quiet" onClick={() => go({ page: 'calendar', month: null })}>Today</button>
          ) : null}
        </div>
        <div className="cal-tools">
          {homes.length > 1 ? (
            <select aria-label="Which home" value={homeFilter} onChange={(e) => setHomeFilter(e.target.value)}>
              <option value="">All homes</option>
              {homes.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          ) : null}
          <span className="cal-legend">
            <span className="pill late">Late</span>
            <span className="pill upcoming">Due</span>
            <span className="pill met">Done</span>
          </span>
        </div>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {lateCount ? (
        <p className="notice">
          {lateCount} deadline{lateCount === 1 ? '' : 's'} this month {lateCount === 1 ? 'has' : 'have'} come with nothing recorded.
        </p>
      ) : null}
      {events && !events.length ? (
        <p className="muted">
          Nothing this month. Deadlines appear once a home has a cycle start date, which is set when the home is added.
        </p>
      ) : null}

      <div className="cal-grid" role="grid" aria-label={grid.label}>
        {WEEKDAYS.map((d) => <div key={d} className="cal-head" role="columnheader">{d}</div>)}
        {grid.days.map((day) => {
          const list = byDay.get(day) ?? [];
          const inMonth = day.startsWith(shown);
          const classes = ['cal-day'];
          if (!inMonth) classes.push('other');
          if (day === today) classes.push('today');
          if (day === selected) classes.push('selected');
          return (
            <div key={day} className={classes.join(' ')} role="gridcell">
              <button type="button" className="cal-date" onClick={() => setSelected(day)} aria-label={longDay(day)}>
                {dayOf(day).getDate()}
              </button>
              {list.slice(0, SHOWN_PER_DAY).map((e) => (
                <EventPill key={eventKey(e)} event={e} onOpen={open} compact />
              ))}
              {list.length > SHOWN_PER_DAY ? (
                <button type="button" className="cal-more" onClick={() => setSelected(day)}>
                  {list.length - SHOWN_PER_DAY} more
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {selected ? (
        <div className="cal-daylist">
          <h2>{longDay(selected)}</h2>
          {(byDay.get(selected) ?? []).length ? (
            <ul className="list">
              {(byDay.get(selected) ?? []).map((e) => (
                <li key={eventKey(e)}>
                  <EventPill event={e} onOpen={open} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Nothing on this day.</p>
          )}
        </div>
      ) : null}

      {/* The narrow-screen version: just the days that have something on them. */}
      <div className="cal-agenda">
        {events && !agendaDays.length ? <p className="muted">Nothing this month.</p> : null}
        {agendaDays.map((day) => (
          <div key={day} className={day === today ? 'today' : ''}>
            <h3>{longDay(day)}</h3>
            <ul className="list">
              {(byDay.get(day) ?? []).map((e) => (
                <li key={eventKey(e)}>
                  <EventPill event={e} onOpen={open} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function EventPill({ event, onOpen, compact = false }: { event: CalendarEvent; onOpen: (e: CalendarEvent) => void; compact?: boolean }) {
  const done = event.state === 'met' || event.state === 'happened';
  const status = event.state === 'late' ? 'Late: ' : event.state === 'met' ? 'Done: ' : '';
  return (
    <button
      type="button"
      className={`pill ${event.state === 'happened' ? 'met' : event.state}${compact ? ' compact' : ''}`}
      title={`${event.homeName} — ${event.label}`}
      onClick={() => onOpen(event)}
    >
      <span className="pill-home">
        {done ? <span aria-hidden="true">✓ </span> : null}
        <span className="visually-hidden">{status}</span>
        {event.homeName}
      </span>
      <span className="pill-label">{event.label}</span>
    </button>
  );
}

function eventKey(e: CalendarEvent): string {
  return [e.date, e.homeId, e.kind, e.cycleStart ?? '', e.deliveryId ?? ''].join('|');
}

/* Monday before the 1st to the Sunday after the last day: always whole weeks. */
function gridFor(month: string): { label: string; days: string[] } {
  const first = `${month}-01`;
  const start = addDays(first, -((dayOf(first).getDay() + 6) % 7));
  const [year, m] = month.split('-').map(Number) as [number, number];
  const lastOfMonth = `${month}-${String(new Date(year, m, 0).getDate()).padStart(2, '0')}`;
  const end = addDays(lastOfMonth, 6 - ((dayOf(lastOfMonth).getDay() + 6) % 7));

  const days: string[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  return {
    label: dayOf(first).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
    days,
  };
}

function stepMonth(month: string, by: number): string {
  const [year, m] = month.split('-').map(Number) as [number, number];
  const when = new Date(year, m - 1 + by, 1, 12);
  return `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, '0')}`;
}
